// src/seo/schemaBuilders.js
// FleetTrack Holland — pure JSON-LD builders.
// Each export returns a serialisable object for inlining via SEOHead schemas prop.
// Ported from 05-deploy/src/seo/schema-builders.ts (TypeScript) to plain JS.

import trackerSchemas from './trackerSchemas.json';

const BASE = 'https://www.fleettrackholland.nl';
const ORG_ID = `${BASE}/#organization`;

/**
 * Site-wide Organization + WebSite graph.
 * NOTE: Already emitted statically in public/index.html as <script type="application/ld+json">.
 * Do NOT call this from a page-level schemas prop (would duplicate). Kept here for
 * completeness so pages that conditionally need it (e.g. SSR fallback) can reach it.
 */
export function buildOrganizationGraph() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': ['Organization', 'LocalBusiness', 'ProfessionalService'],
        '@id': ORG_ID,
        name: 'FleetTrack Holland',
        url: BASE,
        logo: { '@type': 'ImageObject', '@id': `${BASE}/#logo`, url: `${BASE}/logo.png`, width: 300, height: 80, caption: 'FleetTrack Holland' },
        image: { '@id': `${BASE}/#logo` },
        description: 'Specialist in GPS tracking, rittenregistratie en fleet management voor het Nederlandse MKB. Zelf te installeren trackers en Wialon platform. Vanaf 9,95 euro per voertuig per maand.',
        telephone: '+31627246429',
        email: 'info@fleettrackholland.nl',
        taxID: '88606902',
        vatID: 'NL004672676B48',
        iso6523Code: '0106:88606902',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Rotterdam',
          addressRegion: 'Zuid-Holland',
          addressCountry: 'NL',
        },
        geo: { '@type': 'GeoCoordinates', latitude: 51.9058, longitude: 4.5153 },
        priceRange: 'EUR 9,95-100 per voertuig per maand',
        areaServed: [{ '@type': 'Country', name: 'Netherlands' }],
      },
      {
        '@type': 'WebSite',
        '@id': `${BASE}/#website`,
        url: BASE,
        name: 'FleetTrack Holland',
        publisher: { '@id': ORG_ID },
        inLanguage: 'nl-NL',
      },
    ],
  };
}

/** BreadcrumbList — items: [{ name, url }, ...] */
export function buildBreadcrumbList(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Service schema with provider link to site-wide Organization.
 *
 * AggregateRating is intentionally NOT emitted: ratings require a verifiable
 * external review source (Google Business Profile) which does not exist yet.
 * @param {object} opts
 * @param {string} opts.url
 * @param {string} opts.serviceType
 * @param {string} opts.name
 * @param {string} opts.description
 * @param {number} [opts.startingPrice]
 * @param {string} [opts.unit]
 * @param {string} [opts.audience]
 */
export function buildService(opts) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${opts.url}#service`,
    serviceType: opts.serviceType,
    name: opts.name,
    description: opts.description,
    provider: { '@id': ORG_ID },
    areaServed: { '@type': 'Country', name: 'Netherlands' },
    ...(opts.audience ? { audience: { '@type': 'BusinessAudience', audienceType: opts.audience } } : {}),
    category: 'Fleet Management Software',
    ...(opts.startingPrice
      ? {
          offers: {
            '@type': 'Offer',
            price: opts.startingPrice.toString(),
            priceCurrency: 'EUR',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: opts.startingPrice.toString(),
              priceCurrency: 'EUR',
              unitCode: 'MON',
              referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitText: opts.unit || 'voertuig' },
            },
            availability: 'https://schema.org/InStock',
            url: `${BASE}/prijzen/`,
          },
        }
      : {}),
  };
}

/**
 * Product schema for pricing-page SKUs.
 * @param {object} opts
 * @param {string} opts.name
 * @param {string} opts.url
 * @param {string[]} opts.image
 * @param {string} opts.description
 * @param {string} opts.sku
 * @param {string} [opts.mpn]
 * @param {string} opts.brand
 * @param {number} opts.price
 * @param {string} [opts.priceValidUntil]
 * @param {{ value: number, count: number }} [opts.rating]
 */
export function buildProduct(opts) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: opts.name,
    image: opts.image,
    description: opts.description,
    sku: opts.sku,
    mpn: opts.mpn || opts.sku,
    brand: { '@type': 'Brand', name: opts.brand },
    offers: {
      '@type': 'Offer',
      url: opts.url,
      priceCurrency: 'EUR',
      price: opts.price.toFixed(2),
      priceValidUntil: opts.priceValidUntil || '2026-12-31',
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@id': ORG_ID },
    },
    ...(opts.rating
      ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: opts.rating.value.toString(), reviewCount: opts.rating.count } }
      : {}),
  };
}

/** FAQPage schema — qa: [{ q, a }, ...] */
export function buildFAQ(qa) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qa.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
}

/**
 * Article (BlogPosting) schema for /blog/:slug pages.
 * @param {object} opts
 * @param {string} opts.url
 * @param {string} opts.headline
 * @param {string} opts.description
 * @param {string[]} opts.image
 * @param {string} opts.datePublished      ISO 8601 — "2026-05-04" or full timestamp
 * @param {string} [opts.dateModified]     ISO 8601, defaults to datePublished
 * @param {{ name: string, jobTitle?: string, url?: string, image?: string, linkedIn?: string }} opts.author
 * @param {string} [opts.articleSection]
 * @param {string} [opts.keywords]
 * @param {number} [opts.wordCount]
 */
export function buildArticle(opts) {
  return {
    '@context': 'https://schema.org',
    '@type': ['Article', 'BlogPosting'],
    '@id': `${opts.url}#article`,
    headline: opts.headline,
    description: opts.description,
    image: opts.image,
    datePublished: opts.datePublished,
    dateModified: opts.dateModified || opts.datePublished,
    author: {
      '@type': 'Person',
      name: opts.author.name,
      ...(opts.author.jobTitle ? { jobTitle: opts.author.jobTitle } : {}),
      ...(opts.author.url ? { url: opts.author.url } : {}),
      ...(opts.author.image ? { image: opts.author.image } : {}),
      ...(opts.author.linkedIn ? { sameAs: [opts.author.linkedIn] } : {}),
    },
    publisher: { '@id': ORG_ID },
    mainEntityOfPage: { '@type': 'WebPage', '@id': opts.url },
    ...(opts.articleSection ? { articleSection: opts.articleSection } : {}),
    ...(opts.keywords ? { keywords: opts.keywords } : {}),
    ...(opts.wordCount ? { wordCount: opts.wordCount } : {}),
    inLanguage: 'nl-NL',
  };
}

/**
 * LocalBusiness branch schema for city landing pages.
 * @param {string} city          e.g. "Eindhoven"
 * @param {string} region        e.g. "Noord-Brabant"
 * @param {string[]} alsoServed  surrounding municipalities
 */
export function buildLocalBusinessBranch(city, region, alsoServed = []) {
  const slug = city.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '');
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${BASE}/${slug}/#localbusiness`,
    name: `FleetTrack Holland — GPS Tracking ${city}`,
    description: `GPS tracking, rittenregistratie en fleet management voor bedrijven in ${city} en ${region}.`,
    url: `${BASE}/${slug}/`,
    image: `${BASE}/images/cities/${slug}-hero.jpg`,
    telephone: '+31627246429',
    parentOrganization: { '@id': ORG_ID },
    areaServed: [
      { '@type': 'City', name: city },
      ...alsoServed.map((c) => ({ '@type': 'City', name: c })),
    ],
    address: {
      '@type': 'PostalAddress',
      addressLocality: city,
      addressRegion: region,
      addressCountry: 'NL',
    },
    openingHoursSpecification: [
      { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '08:30', closes: '17:30' },
    ],
    priceRange: 'EUR 9,95-100 per voertuig per maand',
  };
}

/**
 * Person schema for named experts/authors (E-E-A-T + AI-citation).
 * Links to Organization via `worksFor`.
 * @param {object} opts
 * @param {string} opts.personId           URL-friendly slug for @id (e.g. "mark-de-vries")
 * @param {string} opts.name               Display name (e.g. "Mark de Vries")
 * @param {string} [opts.jobTitle]         Professional title (defaults to generic)
 * @param {string[]} [opts.knowsAbout]     Areas of expertise (defaults to GPS/tracking topics)
 * @param {string} [opts.url]              About page URL (defaults to /over-ons/)
 */
export function buildPersonSchema(opts) {
  const defaults = {
    jobTitle: 'GPS Tracking Specialist',
    knowsAbout: ['GPS tracking', 'rittenregistratie', 'fleet management', 'wagenparkbeheer'],
    url: `${BASE}/over-ons/`,
  };
  const merged = { ...defaults, ...opts };

  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${BASE}/#person/${merged.personId}`,
    name: merged.name,
    jobTitle: merged.jobTitle,
    worksFor: { '@id': ORG_ID },
    knowsAbout: merged.knowsAbout,
    url: merged.url,
  };
}

/**
 * SoftwareApplication schema for the SaaS product (fleet tracking platform).
 * Exposes price so Google can render a software rich result. AggregateRating
 * is opt-in only and requires a verifiable external review source.
 * @param {object} [opts]
 * @param {number} [opts.price]        Starting monthly price (default 9.95)
 * @param {{ value: number, count: number }} [opts.rating]
 */
export function buildSoftwareApplication(opts = {}) {
  const price = opts.price ?? 9.95;
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `${BASE}/#softwareapplication`,
    name: 'FleetTrack Holland',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Fleet Management Software',
    operatingSystem: 'Web, iOS, Android',
    description:
      'GPS tracking, fiscaal-proof rittenregistratie en fleet management voor het Nederlandse MKB. Real-time tracking, Belastingdienst-proof ritregistratie en wagenparkbeheer vanaf 9,95 euro per voertuig per maand.',
    url: BASE,
    publisher: { '@id': ORG_ID },
    inLanguage: 'nl-NL',
    offers: {
      '@type': 'Offer',
      price: price.toFixed(2),
      priceCurrency: 'EUR',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: price.toFixed(2),
        priceCurrency: 'EUR',
        unitCode: 'MON',
        referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitText: 'voertuig' },
      },
      availability: 'https://schema.org/InStock',
      url: `${BASE}/prijzen/`,
    },
    ...(opts.rating
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: opts.rating.value.toString(),
            reviewCount: opts.rating.count.toString(),
            bestRating: '5',
            worstRating: '1',
          },
        }
      : {}),
  };
}

// OBD-stekker + accu-tracker JSON-LD lives in trackerSchemas.json so the
// post-build injector (scripts/inject-seo-meta.js, Helmet-race fallback) emits
// byte-identical nodes.
/** Service nodes (one per tracker type), anchored on the /hardware/ page. */
export const buildTrackerServices = () => trackerSchemas.services;

/** Homepage OfferCatalog listing the two trackers as Offers (no price). */
export const buildOfferCatalog = () => trackerSchemas.offerCatalog;

export const SITE_BASE = BASE;
export const ORGANIZATION_ID = ORG_ID;
